/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import type {
  InventoryImportMode,
  InventoryImportOptions,
  InventoryImportPreview,
} from '@/types/backend'
import { useContainerSelectQuery } from '@/hooks/useContainers'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCommitInitialStockImportMutation,
  usePreviewInitialStockImportMutation,
} from '@/hooks/useInventory'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

type Props = {
  open: boolean
  onOpenChange: (v: boolean) => void
  defaultContainerId?: string
}

export function ImportInitialStockDialog({
  open,
  onOpenChange,
  defaultContainerId,
}: Props) {
  const previewMut = usePreviewInitialStockImportMutation()
  const commitMut = useCommitInitialStockImportMutation()
  const { data: containers } = useContainerSelectQuery(undefined, true)

  const [file, setFile] = useState<File | null>(null)
  const [delimiter, setDelimiter] = useState(',')
  const [mode, setMode] = useState<InventoryImportMode>('SET')
  const [containerId, setContainerId] = useState<string>('NONE')
  const [autoExpense, setAutoExpense] = useState(false)

  const [columns, setColumns] = useState({
    sku: 'sku',
    name: 'name',
    qty: 'qty',
    containerCode: 'containerCode',
    containerName: 'containerName',
  })

  useEffect(() => {
    if (open) setContainerId(defaultContainerId ?? 'NONE')
  }, [open, defaultContainerId])

  const options = useMemo<InventoryImportOptions>(() => {
    return {
      delimiter,
      mode,
      containerId: containerId !== 'NONE' ? containerId : undefined,
      autoExpense,
      columns: {
        sku: columns.sku || undefined,
        name: columns.name || undefined,
        qty: columns.qty || undefined,
        containerCode: columns.containerCode || undefined,
        containerName: columns.containerName || undefined,
      },
    }
  }, [delimiter, mode, containerId, autoExpense, columns])

  const preview = previewMut.data as InventoryImportPreview | undefined
  const unresolved = (preview?.summary as any)?.unresolvedItemsBySku ?? 0

  const canPreview = !!file && !previewMut.isPending && !commitMut.isPending
  const canCommit =
    !!file &&
    !!preview &&
    (preview.summary?.invalid ?? 0) === 0 &&
    unresolved === 0 &&
    !commitMut.isPending

  const reset = () => {
    setFile(null)
    previewMut.reset()
    commitMut.reset()
  }

  const doPreview = async () => {
    if (!file) return
    try {
      const res = await previewMut.mutateAsync({ file, options })
      toast.success('Preview OK', {
        description: `${res.summary.total} lignes — ${res.summary.valid} OK — ${
          res.summary.invalid
        } erreurs — Produits introuvables: ${
          (res.summary as any).unresolvedItemsBySku ?? 0
        }`,
      })
    } catch (e: any) {
      toast.error('Preview échoué', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  const doCommit = async () => {
    if (!file) return

    // ✅ garde-fous UX (évite "preview ok" puis 404)
    if (!preview) {
      toast.error('Faites un preview avant d’importer.')
      return
    }
    if ((preview.summary?.invalid ?? 0) > 0) {
      toast.error('Corrigez les erreurs du CSV puis refaites Preview.')
      return
    }
    if (unresolved > 0) {
      toast.error('Certains produits sont introuvables', {
        description:
          'Importez d’abord ces produits dans Produits, puis relancez le stock.',
      })
      return
    }

    try {
      const res = await commitMut.mutateAsync({ file, options })
      toast.success('Import terminé', {
        description: `${res.adjusted ?? 0} lignes appliquées — mode: ${
          res.mode ?? options.mode
        }`,
      })
      onOpenChange(false)
      reset()
    } catch (e: any) {
      toast.error('Import échoué', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        if (!v) reset()
      }}
      title='Importer stock initial (CSV)'
      description='Faites un preview, corrigez, puis importez.'
      footer={
        <div className='flex items-center gap-2'>
          <Button
            type='button'
            variant='secondary'
            disabled={!canPreview}
            onClick={doPreview}
          >
            Preview
          </Button>
          <ModalActions
            onConfirm={doCommit}
            confirmLabel='Importer'
            confirmLoading={commitMut.isPending}
            confirmDisabled={!canCommit}
          />
        </div>
      }
    >
      <div className='px-4 py-2 space-y-4'>
        <div className='grid gap-1.5'>
          <Label>Fichier CSV</Label>
          <Input
            type='file'
            accept='.csv,text/csv'
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        {/* ✅ Info claire: ce que fait l'import stock */}
        <Alert>
          <AlertTitle>Ce que fait cet import</AlertTitle>
          <AlertDescription className='space-y-2'>
            <div>
              Cet import sert à importer le <b>stock</b> de produits{' '}
              <b>déjà existants</b> dans la base.
            </div>

            <div className='text-xs text-muted-foreground'>
              <b>Requis</b> : <code>qty</code> (entier ≥ 0) + (<code>sku</code>{' '}
              ou <code>name</code>)
              <br />
              <b>Optionnel</b> : <code>containerCode</code>,{' '}
              <code>containerName</code>
            </div>

            <div className='text-xs text-muted-foreground'>
              <b>Mode</b> : <b>SET</b> remplace le stock, <b>ADD</b> ajoute au
              stock existant.
            </div>

            <div className='text-xs text-muted-foreground'>
              Si aucun conteneur n’est précisé dans le CSV, le stock est
              appliqué au <b>conteneur par défaut</b> (obligatoire côté
              backend).
            </div>
          </AlertDescription>
        </Alert>

        {/* ✅ Warning si produits introuvables */}
        {preview && unresolved > 0 ? (
          <Alert>
            <AlertTitle>Produits introuvables</AlertTitle>
            <AlertDescription className='space-y-2'>
              <div>
                <b>{unresolved}</b> ligne(s) font référence à des produits qui
                n’existent pas encore (SKU/nom introuvable).
              </div>
              <div className='text-xs text-muted-foreground'>
                Solution : importez d’abord ces produits dans{' '}
                <b>Produits → Importer CSV</b>, puis relancez ce stock.
              </div>
            </AlertDescription>
          </Alert>
        ) : null}

        <div className='grid gap-3 sm:grid-cols-1'>
          <div className='grid gap-1.5'>
            <Label>Délimiteur</Label>
            <Input
              value={delimiter}
              onChange={(e) => setDelimiter(e.target.value)}
              placeholder=','
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Conteneur (optionnel)</Label>
            <Select value={containerId} onValueChange={setContainerId}>
              <SelectTrigger>
                <SelectValue placeholder='Choisir…' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='NONE'>Auto (selon CSV / défaut)</SelectItem>
                {(containers ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.isDefault ? `★ ${c.name}` : c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* ✅ texte plus direct */}
            <div className='text-xs text-muted-foreground'>
              ⚠️ La sélection de conteneur ici n’est pas appliquée actuellement.
              Utilisez <code>containerCode</code> / <code>containerName</code>{' '}
              dans le CSV.
            </div>
          </div>
        </div>

        <div className='grid gap-3'>
          <div className='grid gap-1.5'>
            <Label>Mode</Label>
            <div className='flex flex-wrap gap-2'>
              <Button
                type='button'
                variant={mode === 'SET' ? 'default' : 'secondary'}
                onClick={() => setMode('SET')}
              >
                Remplacer (SET)
              </Button>
              <Button
                type='button'
                variant={mode === 'ADD' ? 'default' : 'secondary'}
                onClick={() => setMode('ADD')}
              >
                Ajouter (ADD)
              </Button>
            </div>
            <div className='text-xs text-muted-foreground'>
              SET = met le stock à la valeur du CSV. ADD = ajoute au stock
              existant.
            </div>
          </div>

          <div className='flex items-center justify-between rounded-md border p-3'>
            <div className='space-y-0.5'>
              <div className='text-sm font-medium'>
                Créer dépense automatiquement
              </div>
              <div className='text-xs text-muted-foreground'>
                ⚠️ Option non appliquée pour l’instant (sera activée plus tard).
              </div>
            </div>
            <Switch checked={autoExpense} onCheckedChange={setAutoExpense} />
          </div>
        </div>

        <div className='rounded-md border p-3 space-y-3'>
          <div className='text-sm font-medium'>Colonnes (optionnel)</div>

          <div className='text-xs text-muted-foreground'>
            Si ton CSV a exactement les bons headers, laisse par défaut.
            <br />
            Requis côté CSV : <b>qty</b> + (<b>sku</b> ou <b>name</b>).
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <Field label='sku'>
              <Input
                value={columns.sku}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, sku: e.target.value }))
                }
              />
            </Field>
            <Field label='name'>
              <Input
                value={columns.name}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, name: e.target.value }))
                }
              />
            </Field>
            <Field label='qty'>
              <Input
                value={columns.qty}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, qty: e.target.value }))
                }
              />
            </Field>
            <Field label='containerCode'>
              <Input
                value={columns.containerCode}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, containerCode: e.target.value }))
                }
              />
            </Field>
            <Field label='containerName'>
              <Input
                value={columns.containerName}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, containerName: e.target.value }))
                }
              />
            </Field>
          </div>
        </div>

        {preview ? (
          <div className='rounded-md border p-3 text-sm space-y-2'>
            <div className='font-medium'>Résumé preview</div>
            <div className='text-muted-foreground'>
              Total: <b>{preview.summary.total}</b> — OK:{' '}
              <b>{preview.summary.valid}</b> — Erreurs:{' '}
              <b>{preview.summary.invalid}</b>
              <br />
              Produits introuvables: <b>{unresolved}</b>
            </div>

            {(preview.errors?.length ?? 0) > 0 ? (
              <div className='mt-2'>
                <div className='font-medium text-red-600'>
                  Erreurs (extrait)
                </div>
                <ul className='list-disc pl-5 text-xs'>
                  {preview.errors!.slice(0, 10).map((e, idx) => (
                    <li key={idx}>
                      L{e.index}: {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : preview && unresolved === 0 ? (
              <div className='text-xs text-green-700'>
                Aucune erreur bloquante.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </AppDialog>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className='grid gap-1.5'>
      <Label>{label}</Label>
      {children}
    </div>
  )
}
